import "./index.css";
import dayjs from 'dayjs';

import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";

import { contactPreferences, educationHistory, formFields } from "./formFields";
import {
  Button,
  Card,
  Checkbox,
  DatePicker,
  Form,
  Input,
  Select,
  Spin,
  Row,
  Col,
  Typography,
  Space,
  Radio,
  notification,
  Tooltip,
} from "antd";
import { 
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  WechatOutlined,
  LinkedinOutlined,
  GlobalOutlined,
  EnvironmentOutlined,
  BankOutlined,
  BookOutlined,
  HeartOutlined,
  SaveOutlined,
  EditOutlined,
  PlusOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";

import MenuLayout from "../../components/Menu";
import Loading from "../../components/Loading";
import { supabase } from "../../lib/supabase";

const { Title, Text } = Typography;

const selectOptions = [
  {
    label: "所有校友",
    value: 1,
  },
  {
    label: "同届",
    value: 2,
  },
  {
    label: "同班",
    value: 3,
  },
  {
    label: "隐藏",
    value: 0,
  },
];
const yearTypeColumns = new Set(
  educationHistory
    .filter((item) => item.type === "year")
    .map((item) => item.key)
);

const MyProfile = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [activeSchools, setActiveSchools] = useState([]);

  useEffect(() => {
    checkUserStatus();
    query_self();
  }, []);

  const checkUserStatus = async () => {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      navigate("/login");
    }
  };

  // Utility function to check if a value is empty
  const isEmptyValue = (value) => {
    return value === undefined || value === null || value === '';
  };

  // Utility function to convert all empty strings to null
  const convertEmptyStringsToNull = (formValues) => {
    const updatedValues = { ...formValues };
    
    // Convert all empty string values to null
    Object.keys(updatedValues).forEach(key => {
      if (updatedValues[key] === '') {
        updatedValues[key] = null;
      }
    });
    
    return updatedValues;
  };

  // Utility function to auto-clear pflags when all related fields are empty
  const clearEmptyPflags = (formValues) => {
    const updatedValues = { ...formValues };
    const clearedPflags = [];

    // Clear individual contact pflags when their corresponding fields are empty
    const contactMappings = [
      { field: 'pub_email', pflag: 'p_pub_email' },
      { field: 'phone', pflag: 'p_phone' },
      { field: 'wechat_id', pflag: 'p_wechat_id' },
      { field: 'linkedin', pflag: 'p_linkedin' },
      { field: 'homepage_link', pflag: 'p_homepage_link' },
      { field: 'other_contact', pflag: 'p_other_contact' }
    ];

    contactMappings.forEach(({ field, pflag }) => {
      if (isEmptyValue(updatedValues[field])) {
        updatedValues[pflag] = null;
        clearedPflags.push(pflag);
      }
    });

    // Clear location pflag when both location fields are empty
    if (isEmptyValue(updatedValues.loc_country) && isEmptyValue(updatedValues.loc_city)) {
      updatedValues.p_loc = null;
      clearedPflags.push('p_loc');
    }

    // Clear career pflag when all career fields are empty
    if (isEmptyValue(updatedValues.employer) && 
        isEmptyValue(updatedValues.job_field) && 
        isEmptyValue(updatedValues.job_desc)) {
      updatedValues.p_career = null;
      clearedPflags.push('p_career');
    }

    // Clear school pflags when all school-related fields are empty for each school
    for (let i = 1; i <= 4; i++) {
      const schoolFields = [
        `school${i}`,
        `school${i}_start_time`,
        `school${i}_end_time`,
        `school${i}_level`,
        `school${i}_major`
      ];
      
      const allSchoolFieldsEmpty = schoolFields.every(field => isEmptyValue(updatedValues[field]));
      if (allSchoolFieldsEmpty) {
        updatedValues[`p_school${i}`] = null;
        clearedPflags.push(`p_school${i}`);
      }
    }

    if (clearedPflags.length > 0) {
      console.log("[Profile Update] Auto-cleared privacy flags:", clearedPflags);
    }

    return updatedValues;
  };

  const handleFinishForm = async (formValues) => {
    console.log("[Profile Update] Starting profile update process");
    console.log("[Profile Update] Raw form values:", Object.keys(formValues).length, "fields");
    
    setSubmitLoading(true);
    const { name_cn, ...remainingFormValues } = formValues;

    // Convert all empty strings to null
    const nullifiedFormValues = convertEmptyStringsToNull(remainingFormValues);
    console.log("[Profile Update] Nullified empty strings");

    // Process year fields
    const yearFieldsProcessed = [];
    for (let key in nullifiedFormValues) {
      if (yearTypeColumns.has(key) && nullifiedFormValues[key]) {
        nullifiedFormValues[key] = nullifiedFormValues[key].year();
        yearFieldsProcessed.push(key);
      }
    }
    if (yearFieldsProcessed.length > 0) {
      console.log("[Profile Update] Processed year fields:", yearFieldsProcessed);
    }

    // Clean numeric fields that should be integers
    const numericFields = ['schoolm_class', 'schoolh_class', 'schoolm_start_time', 'schoolm_end_time', 'schoolh_start_time', 'schoolh_end_time'];
    const numericFieldsProcessed = [];
    numericFields.forEach(field => {
      if (nullifiedFormValues[field] !== undefined && nullifiedFormValues[field] !== null && nullifiedFormValues[field] !== '') {
        // Clean and parse numeric values, removing any non-numeric characters except negative sign
        const cleanedValue = String(nullifiedFormValues[field]).replace(/[^\d-]/g, '');
        const parsedValue = parseInt(cleanedValue, 10);
        nullifiedFormValues[field] = isNaN(parsedValue) ? null : parsedValue;
        numericFieldsProcessed.push(field);
      } else {
        nullifiedFormValues[field] = null;
      }
    });
    if (numericFieldsProcessed.length > 0) {
      console.log("[Profile Update] Processed numeric fields:", numericFieldsProcessed);
    }

    // Ensure all school fields (1-4) are present with null for undefined ones
    for (let i = 1; i <= 4; i++) {
      const schoolFields = [
        `school${i}`,
        `school${i}_start_time`,
        `school${i}_end_time`,
        `school${i}_level`,
        `school${i}_major`,
        `p_school${i}`
      ];
      
      schoolFields.forEach(field => {
        if (nullifiedFormValues[field] === undefined) {
          nullifiedFormValues[field] = null;
        }
      });
    }

    // Auto-clear pflags when all related fields are empty
    const processedFormValues = clearEmptyPflags(nullifiedFormValues);
    console.log("[Profile Update] Prepared final payload with", Object.keys(processedFormValues).length, "fields");

    console.log("[Profile Update] Calling update_self RPC function");
    const { data, error } = await supabase.rpc(
      "update_self",
      processedFormValues
    );
    console.log("[Profile Update] RPC call completed - Status:", data?.status, "Error:", error ? "Present" : "None");
    
    // Show success notification if the API returns status: 1
    if (data && data.status === 1) {
      console.log("[Profile Update] Success - Profile updated successfully");
      notification.success({
        message: '更新成功',
        description: '您的个人资料已成功更新！',
        placement: 'topRight',
        duration: 3,
        icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
      });
    } else if (error) {
      console.error("[Profile Update] Error - Update failed:", error.message || error);
      // Show error notification if there's an error
      notification.error({
        message: '更新失败',
        description: '更新个人资料时出现错误，请稍后重试。',
        placement: 'topRight',
        duration: 4,
        icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
      });
    } else {
      console.warn("[Profile Update] Warning - Unexpected response status:", data?.status);
    }
    
    console.log("[Profile Update] Process completed");
    setSubmitLoading(false);
  };

  const processData = (data) => {
    for (let key in data) {
      if (yearTypeColumns.has(key) && data[key]) {
        data[key] = dayjs(data[key].toString(), "YYYY");
      }
    }

    // Process active schools
    const schools = [];
    for (let i = 1; i <= 4; i++) {
      if (data[`school${i}`]) {
        schools.push(i);
      }
    }
    setActiveSchools(schools);

    return data;
  };

  const handleAddSchool = () => {
    if (activeSchools.length >= 4) return;
    
    const nextSchoolNumber = activeSchools.length + 1;
    setActiveSchools([...activeSchools, nextSchoolNumber]);
    
    // Set default privacy permission to "所有人" (value: 1) for the new school
    form.setFieldValue(`p_school${nextSchoolNumber}`, 1);
  };

  const handleDeleteSchool = (schoolNumber) => {
    const newActiveSchools = activeSchools.filter(school => school !== schoolNumber);
    
    // Clear the deleted school's data
    const formValues = form.getFieldsValue();
    const schoolFields = [
      `school${schoolNumber}`,
      `school${schoolNumber}_start_time`,
      `school${schoolNumber}_end_time`,
      `school${schoolNumber}_level`,
      `school${schoolNumber}_major`,
      `p_school${schoolNumber}`
    ];
    
    schoolFields.forEach(field => {
      form.setFieldValue(field, undefined);
    });

    // Remap remaining schools
    const remappedSchools = newActiveSchools.map((school, index) => {
      const newSchoolNumber = index + 1;
      if (school !== newSchoolNumber) {
        // Move data from old school number to new school number
        const oldSchoolFields = [
          `school${school}`,
          `school${school}_start_time`,
          `school${school}_end_time`,
          `school${school}_level`,
          `school${school}_major`,
          `p_school${school}`
        ];
        
        const newSchoolFields = [
          `school${newSchoolNumber}`,
          `school${newSchoolNumber}_start_time`,
          `school${newSchoolNumber}_end_time`,
          `school${newSchoolNumber}_level`,
          `school${newSchoolNumber}_major`,
          `p_school${newSchoolNumber}`
        ];

        oldSchoolFields.forEach((oldField, fieldIndex) => {
          const value = form.getFieldValue(oldField);
          if (value !== undefined) {
            form.setFieldValue(newSchoolFields[fieldIndex], value);
            form.setFieldValue(oldField, undefined);
          }
        });
      }
      return newSchoolNumber;
    });

    setActiveSchools(remappedSchools);
  };

  const query_self = async () => {
    console.log("[Profile Load] Starting profile data load");
    setLoading(true);
    
    const { data, error } = await supabase.rpc("query_self", {});
    
    if (error) {
      console.error("[Profile Load] Error loading profile:", error.message || error);
    } else if (data?.data?.[0]) {
      console.log("[Profile Load] Profile data loaded successfully");
      const processedData = processData(data.data[0]);
      form.setFieldsValue(processedData);
      console.log("[Profile Load] Form populated with", Object.keys(processedData).length, "fields");
    } else {
      console.warn("[Profile Load] No profile data found");
    }
    
    setLoading(false);
    console.log("[Profile Load] Load process completed");
  };

  const getFormItem = (item) => {
    // Check if this is a middle/high school field that should be disabled
    const isMiddleHighSchoolField = ['schoolm', 'schoolm_class', 'schoolm_start_time', 'schoolm_end_time', 
                                     'schoolh', 'schoolh_class', 'schoolh_start_time', 'schoolh_end_time'].includes(item.key);
    
    // Get current form values to check if field has existing data
    const currentFormValues = form.getFieldsValue();
    const hasExistingValue = currentFormValues[item.key] !== undefined && 
                            currentFormValues[item.key] !== null && 
                            currentFormValues[item.key] !== '';
    
    // Determine if field should be disabled
    const shouldDisable = item.disabled || (isMiddleHighSchoolField && hasExistingValue);
    
    // Create label with tooltip for disabled middle/high school fields
    const createLabel = () => {
      const baseLabel = (
        <Space>
          {getFieldIcon(item.key)}
          <Text strong>{item.label}</Text>
        </Space>
      );
      
      if (shouldDisable && isMiddleHighSchoolField && hasExistingValue) {
        return (
          <Tooltip title="已填写，如需修改请联系管理员">
            <Space>
              {baseLabel}
              <InfoCircleOutlined style={{ color: '#1890ff', fontSize: '14px' }} />
            </Space>
          </Tooltip>
        );
      }
      
      return baseLabel;
    };

    if (item.type === "input") {
      // Special handling for misc_text to use TextArea
      if (item.key === "misc_text") {
        return (
          <Form.Item 
            label={createLabel()} 
            key={item.key} 
            name={item.key}
            className="modern-form-item"
          >
            <Input.TextArea 
              disabled={shouldDisable} 
              size="large"
              className="modern-input"
              placeholder={`请输入${item.label}`}
              rows={6}
              showCount
              maxLength={500}
              allowClear
            />
          </Form.Item>
        );
      }
      
      return (
        <Form.Item 
          label={createLabel()} 
          key={item.key} 
          name={item.key}
          className="modern-form-item"
        >
          <Input 
            disabled={shouldDisable} 
            size="large"
            className="modern-input"
            placeholder={`请输入${item.label}`}
            allowClear
          />
        </Form.Item>
      );
    }
    if (item.type === "select" && item.options) {
      return (
        <Form.Item 
          label={createLabel()} 
          key={item.key} 
          name={item.key}
          className="modern-form-item"
        >
          <Select 
            className="modern-select" 
            options={item.options} 
            allowClear 
            size="large"
            placeholder={`请选择${item.label}`}
            showSearch
            disabled={shouldDisable}
          />
        </Form.Item>
      );
    }
    if (item.type === "year") {
      return (
        <Form.Item 
          label={createLabel()} 
          key={item.key} 
          name={item.key}
          className="modern-form-item"
        >
          <DatePicker 
            picker="year" 
            size="large"
            className="modern-date-picker"
            placeholder="选择年份"
            allowClear
            disabled={shouldDisable}
          />
        </Form.Item>
      );
    }
  };

  const getFieldIcon = (key) => {
    const iconMap = {
      name_cn: <UserOutlined />,
      pub_email: <MailOutlined />,
      phone: <PhoneOutlined />,
      wechat_id: <WechatOutlined />,
      linkedin: <LinkedinOutlined />,
      homepage_link: <GlobalOutlined />,
      other_contact: <PhoneOutlined />,
      misc_text: <EditOutlined />,
      loc_country: <EnvironmentOutlined />,
      loc_city: <EnvironmentOutlined />,
      employer: <BankOutlined />,
      job_field: <BankOutlined />,
      job_desc: <BankOutlined />,
      schoolm: <BookOutlined />,
      schoolh: <BookOutlined />,
      school1: <BookOutlined />,
      school2: <BookOutlined />,
      school3: <BookOutlined />,
      school4: <BookOutlined />,
    };
    return iconMap[key] || <UserOutlined />;
  };

  const getPrivacyControl = (privacyKey) => {
    if (!privacyKey) return null;
    
    return (
      <Form.Item
        name={privacyKey}
        className="privacy-control-item"
      >
        <Radio.Group 
          size="small"
          className="privacy-radio-group"
          optionType="button"
          buttonStyle="solid"
        >
          <Radio.Button value={1}>所有人</Radio.Button>
          <Radio.Button value={2}>仅同届</Radio.Button>
          <Radio.Button value={3}>仅同班</Radio.Button>
          <Radio.Button value={0}>隐藏</Radio.Button>
        </Radio.Group>
      </Form.Item>
    );
  };

  const renderSchoolSection = (schoolNumber, schoolFields, isPlaceholder = false) => {
    const schoolNameField = formFields.find(field => field.key === `school${schoolNumber}`);
    
    if (isPlaceholder) {
      return (
        <Col xs={24} key={`school-placeholder`}>
          <Card 
            size="small"
            title={
              <Space>
                <BookOutlined />
                <Text strong>添加新学校</Text>
              </Space>
            }
            className="school-sub-card school-placeholder"
            bordered={false}
          >
            <div className="placeholder-content">
              <Button 
                type="dashed" 
                size="large" 
                icon={<PlusOutlined />}
                onClick={() => handleAddSchool()}
                className="add-school-button"
                block
              >
                添加学校信息
              </Button>
            </div>
          </Card>
        </Col>
      );
    }
    
    // Order fields: 学校名称, 入学时间, 结束时间, 学位, 专业
    const orderedFields = [];
    const startTimeField = schoolFields.find(field => field.key === `school${schoolNumber}_start_time`);
    const endTimeField = schoolFields.find(field => field.key === `school${schoolNumber}_end_time`);
    const levelField = schoolFields.find(field => field.key === `school${schoolNumber}_level`);
    const majorField = schoolFields.find(field => field.key === `school${schoolNumber}_major`);
    
    if (startTimeField) orderedFields.push(startTimeField);
    if (endTimeField) orderedFields.push(endTimeField);
    if (levelField) orderedFields.push(levelField);
    if (majorField) orderedFields.push(majorField);
    
    return (
      <Col xs={24} key={`school${schoolNumber}`}>
        <Card 
          size="small"
          title={
            <Space>
              <BookOutlined />
              <Text strong>{`学校${schoolNumber}`}</Text>
              <Button 
                type="text" 
                size="small" 
                icon={<DeleteOutlined />}
                onClick={() => handleDeleteSchool(schoolNumber)}
                className="delete-school-button"
                danger
              />
            </Space>
          }
          className="school-sub-card profile-card-with-radio"
          bordered={false}
          extra={getPrivacyControl(`p_school${schoolNumber}`)}
        >
          <Row gutter={[12, 8]}>
            {/* School Name */}
            <Col xs={24}>
              <div className="field-wrapper">
                {getFormItem(schoolNameField)}
              </div>
            </Col>
            
            {/* School Details in specified order - more compact layout */}
            {orderedFields.map((item) => (
              <Col xs={12} sm={12} md={6} key={item.key}>
                <div className="field-wrapper">
                  {getFormItem(item)}
                </div>
              </Col>
            ))}
          </Row>
        </Card>
      </Col>
    );
  };

  // Group form fields by category
  const basicInfoFields = formFields.filter(field => 
    ['name_cn', 'pub_email', 'phone', 'wechat_id', 'linkedin', 'homepage_link', 'other_contact'].includes(field.key)
  );
  
  const miscTextFields = formFields.filter(field => 
    ['misc_text'].includes(field.key)
  );
  
  const locationFields = formFields.filter(field => 
    ['loc_country', 'loc_city'].includes(field.key)
  );
  
  const careerFields = formFields.filter(field => 
    ['employer', 'job_field', 'job_desc'].includes(field.key)
  );
  
  const educationFields = formFields.filter(field => 
    ['school1', 'school2', 'school3', 'school4'].includes(field.key)
  );

  // Group education history by category
  const middleHighSchoolFields = educationHistory.filter(field => 
    ['schoolm', 'schoolm_class', 'schoolm_start_time', 'schoolm_end_time', 
     'schoolh', 'schoolh_class', 'schoolh_start_time', 'schoolh_end_time'].includes(field.key)
  );
  
  // Group higher education fields by school
  const school1Fields = educationHistory.filter(field => field.key.startsWith('school1'));
  const school2Fields = educationHistory.filter(field => field.key.startsWith('school2'));
  const school3Fields = educationHistory.filter(field => field.key.startsWith('school3'));
  const school4Fields = educationHistory.filter(field => field.key.startsWith('school4'));
  
  const higherEducationFields = educationHistory.filter(field => 
    ['school1', 'school2', 'school3', 'school4'].some(school => field.key.startsWith(school))
  );

  return (
    <MenuLayout>
      <div className="profile-container">
        <div className="profile-header">
          <Title level={2} className="profile-title">
            <UserOutlined /> 个人资料
          </Title>
          <Text type="secondary">管理您的个人信息和隐私设置</Text>
        </div>

        <Spin
          tip="加载中..."
          indicator={Loading}
          spinning={loading || submitLoading}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleFinishForm}
            className="modern-profile-form"
          >
            {/* Basic Information Card */}
            <Card 
              title={
                <Space>
                  <UserOutlined />
                  <Text strong>基本信息</Text>
                </Space>
              }
              className="profile-card"
              bordered={false}
            >
              <Row gutter={[32, 24]}>
                {basicInfoFields.map((item) => (
                  <Col xs={24} sm={12} lg={8} key={item.key}>
                    <div className="field-wrapper">
                      {getFormItem(item)}
                      {getPrivacyControl(item.privacy_control_key)}
                    </div>
                  </Col>
                ))}
              </Row>
            </Card>

            {/* Additional Information Card */}
            <Card 
              title={
                <Space>
                  <EditOutlined />
                  <Text strong>其他信息</Text>
                </Space>
              }
              className="profile-card"
              bordered={false}
            >
              <Row gutter={[32, 24]}>
                {miscTextFields.map((item) => (
                  <Col xs={24} key={item.key}>
                    <div className="field-wrapper">
                      {getFormItem(item)}
                    </div>
                  </Col>
                ))}
              </Row>
            </Card>

            {/* Middle & High School Information Card */}
            <Card 
              title={
                <Space>
                  <BookOutlined />
                  <Text strong>初中 & 高中信息</Text>
                </Space>
              }
              className="profile-card"
              bordered={false}
            >
              <div style={{ marginBottom: '16px' }}>
                <Text type="secondary" style={{ fontSize: '14px' }}>
                  <InfoCircleOutlined style={{ marginRight: '8px' }} />
                  注：班级信息仅能填写一次，之后不能自行更改，如需更改请联系管理员
                </Text>
              </div>
              <Row gutter={[32, 24]}>
                {middleHighSchoolFields.map((item) => (
                  <Col xs={24} sm={12} md={8} lg={6} key={item.key}>
                    <div className="field-wrapper">
                      {getFormItem(item)}
                    </div>
                  </Col>
                ))}
              </Row>
            </Card>

            {/* Location Information Card */}
            <Card 
              title={
                <Space>
                  <EnvironmentOutlined />
                  <Text strong>位置信息</Text>
                </Space>
              }
              extra={getPrivacyControl('p_loc')}
              className="profile-card profile-card-with-radio"
              bordered={false}
            >
              <Row gutter={[32, 24]}>
                {locationFields.map((item) => (
                  <Col xs={24} sm={12} key={item.key}>
                    <div className="field-wrapper">
                      {getFormItem(item)}
                    </div>
                  </Col>
                ))}
              </Row>
            </Card>

            {/* Career Information Card */}
            <Card 
              title={
                <Space>
                  <BankOutlined />
                  <Text strong>职业信息</Text>
                </Space>
              }
              extra={getPrivacyControl('p_career')}
              className="profile-card profile-card-with-radio"
              bordered={false}
            >
              <Row gutter={[32, 24]}>
                {careerFields.map((item) => (
                  <Col xs={24} sm={12} lg={8} key={item.key}>
                    <div className="field-wrapper">
                      {getFormItem(item)}
                    </div>
                  </Col>
                ))}
              </Row>
            </Card>

            {/* Education History Card */}
            <Card 
              title={
                <Space>
                  <BookOutlined />
                  <Text strong>高等教育背景</Text>
                </Space>
              }
              className="profile-card"
              bordered={false}
            >
              <Row gutter={[16, 16]}>
                {activeSchools.map((schoolNumber) => {
                  const schoolFields = educationHistory.filter(field => field.key.startsWith(`school${schoolNumber}`));
                  return renderSchoolSection(schoolNumber, schoolFields);
                })}
                {activeSchools.length < 4 && renderSchoolSection(null, null, true)}
              </Row>
            </Card>

            {/* Contact Preferences Card */}
            <Card 
              title={
                <Space>
                  <HeartOutlined />
                  <Text strong>联系方式偏好</Text>
                </Space>
              }
              className="profile-card"
              bordered={false}
            >
              <Row gutter={[24, 16]}>
                {contactPreferences.map((item) => (
                  <Col xs={24} sm={12} lg={8} key={item.key}>
                    <Form.Item
                      name={item.key}
                      valuePropName="checked"
                      className="preference-item"
                    >
                      <Checkbox className="modern-checkbox">
                        <Text>{item.label}</Text>
                      </Checkbox>
                    </Form.Item>
                  </Col>
                ))}
              </Row>
            </Card>

            {/* Submit Button */}
            <div className="submit-section">
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={submitLoading}
                size="large"
                icon={<SaveOutlined />}
                className="submit-button"
              >
                保存更改
              </Button>
            </div>
          </Form>
        </Spin>
      </div>
    </MenuLayout>
  );
};

export default MyProfile;
